import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { StudentTable } from '@uplift/queries';
import { Button, FlexTable } from '@uplift/ui-v2';

import { convertStudentsToRows } from './helpers';
import { useStudentsTableColumns } from './useStudentsTableColumns';

export type StudentsTableProps = {
  orgId?: string;
  students: StudentTable[];
  onReferStudent?: () => void;
  renderSearchBar: (onResetPage: () => void) => JSX.Element;
  isCareApp?: boolean;
};

export const StudentsTable: React.VFC<StudentsTableProps> = ({
  students,
  onReferStudent,
  renderSearchBar,
  isCareApp = false,
}) => {
  const { classes } = useStudentsTableStyles();
  const columns = useStudentsTableColumns(isCareApp);
  const rows = convertStudentsToRows(students);

  const pageTitle = isCareApp ? (
    <Typography className={classes.title} data-testid="students-table-title">
      Students
    </Typography>
  ) : (
    <Typography variant="h4" data-testid="clients-table-title">
      Clients
    </Typography>
  );

  const studentImage = (
    <img
      src="https://scdn.joinuplift.co/public/application-assets/students.png"
      alt="students"
      className={classes.image}
    />
  );

  const careAppEmptyOverlayProps = {
    message: (
      <Typography variant="h6" data-testid="students-table-empty-state">
        Let&rsquo;s start
        <span className={classes.secondaryColor}> supporting your students</span>
      </Typography>
    ),
    icon: studentImage,
    subtitle: `We work with you to support your students’ mental
      health as they go through life’s changes, so they can
      succeed—academically and in life.`,
    className: classes.overlay,
    button: (
      <Button variant="outlined" color="secondary" onClick={onReferStudent} data-testid="students-table-refer-button">
        Refer a Student
      </Button>
    ),
  };

  const adminAppEmptyOverlayProps = {
    message: (
      <Typography variant="h6" data-testid="clients-table-empty-state">
        No referred clients <span className={classes.secondaryColor}> yet</span>
      </Typography>
    ),
    icon: studentImage,
    className: classes.overlay,
  };

  return (
    <Box className={classes.container}>
      {pageTitle}
      <Paper className={classes.table}>
        <FlexTable
          rows={rows}
          columns={columns}
          emptyOverlayProps={isCareApp ? careAppEmptyOverlayProps : adminAppEmptyOverlayProps}
          renderSearchBar={renderSearchBar}
          initialSort={{
            colId: 'referral',
            type: 'desc',
          }}
        />
      </Paper>
    </Box>
  );
};

const useStudentsTableStyles = makeStyles({ name: 'StudentsTable' })((theme) => ({
  container: {
    maxWidth: '1024px',
    display: 'flex',
    flexDirection: 'column',
    marginBottom: theme.spacing(6),
  },
  table: {
    marginTop: theme.spacing(2),
  },
  overlay: {
    '& h6': {
      color: theme.palette.darkSlate.main,
    },
    '& span': {
      lineHeight: '24px',
    },
    marginBottom: theme.spacing(4),
  },
  title: {
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: '32.02px',
    textAlign: 'left',
  },
  image: {
    width: '243px',
    height: '237px',
  },
  secondaryColor: {
    color: theme.palette.secondary.main,
  },
}));
